import { Stack, Tooltip } from '@mui/material'
import Box from '@mui/material/Box'
import { ReactComponent as HealthLogo } from 'assets/icons/health-logo.svg'
import { ReactComponent as UndigitizedIcon } from 'assets/Undigitized.svg'

import LactationLogo from 'assets/LactationLogo'
import { InspectionStatus, TreatmentStatus } from 'models'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import InfoChipItem from 'ui/InfoChipItem'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { ReactComponent as ArrowBackIosIcon } from '../assets/arrow_back_logo.svg'
import { ReactComponent as EditLogo } from '../assets/table_edit_logo.svg'
import { ReactComponent as TagNumberIcon } from '../assets/icons/tag-icon.svg'
import { fetchGroupList } from '../store/groupsSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { COLORS } from '../styles/colors'
import { TypographyHeaderExtra } from '../ui/typography/TypographyHeaderExtra'

import ButtonFunc from 'ui/buttons/ButtonFunc'
import TooltipHover from 'ui/TooltipHover'
import { useGetMilkersInfo } from 'hook/query/useGetMilkersInfo'
import { leaveCheckupPage } from 'utils/checkupWindow'
import { hideDigitizationUi } from 'utils/companyDigitizationFeatures'
import { DashboardEditControls } from './farm-dashboard/components/DashboardEditControls/DashboardEditControls'
import { StatusGroupLabels } from './groups/ui/status-labels/StatusLabels'
import {
	AssignShiftDrawer,
	AssignShiftInitialValues,
} from './milking-process/shifts-process/components/assign-shift-drawer/AssignShiftDrawer'
import ActiveComp from './rationSystem/utils/ActiveComp'
import PopupRationAddComp from './shared/PopupRationAddComp'
import SettingUser from './settings/components/setting-users/components/SettingUser'
import { getHeaderTitle } from './shared/processes'

const yieldStatusColor = {
	rejected: COLORS.error,
	dry: COLORS.health,
}

const Header = () => {
	const { groupList } = useAppSelector(store => store.groupsReducer)
	const { rationInfo } = useAppSelector(state => state.rationSystemReducer)
	const { cattleStatuses } = useAppSelector(state => state.cattlesSlice)
	const { userData, userDataPending } = useAppSelector(
		state => state.userReducer,
	)
	const { permissions } = useAppSelector(state => state.authReducer)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const location = useLocation()
	const urlParams = useParams()
	const isCowPassportPage = location.pathname.startsWith('/cattles/cow/')
	const previousLocationRef = useRef<string | null>(null)
	const cowNavState = location.state as { identified?: boolean } | null
	const identified = cattleStatuses.identified ?? cowNavState?.identified
	const showUndigitizedMark =
		isCowPassportPage &&
		identified === false &&
		!hideDigitizationUi(userData?.company?.id)

	const [isSettingUserOpen, setIsSettingUserOpen] = useState(false)
	const [openAddComp, setOpenAddComp] = useState(false)
	const [isAssignShiftOpen, setIsAssignShiftOpen] = useState(false)
	const [showAssignShiftHint, setShowAssignShiftHint] = useState(false)
	const [assignShiftInitialValues, setAssignShiftInitialValues] =
		useState<AssignShiftInitialValues | null>(null)
	const { data: milkers = [], isLoading: isMilkersLoading } =
		useGetMilkersInfo()
	const canAssignShift = milkers.length > 0
	const isAssignShiftDisabled = isMilkersLoading || !canAssignShift

	useEffect(() => {
		if (groupList.length === 0) {
			dispatch(fetchGroupList())
		}
	}, [dispatch, groupList.length])

	useEffect(() => {
		previousLocationRef.current = location.pathname
	}, [location])

	useEffect(() => {
		const handleOpenAssignShiftFromCell = (event: Event) => {
			const customEvent = event as CustomEvent<AssignShiftInitialValues>
			setAssignShiftInitialValues(customEvent.detail ?? null)
			setIsAssignShiftOpen(true)
		}

		window.addEventListener(
			'open-assign-shift-drawer',
			handleOpenAssignShiftFromCell as EventListener,
		)
		return () => {
			window.removeEventListener(
				'open-assign-shift-drawer',
				handleOpenAssignShiftFromCell as EventListener,
			)
		}
	}, [])

	const currentGroupInfo = groupList?.find(
		el =>
			el.id.toString() === urlParams?.groupkey ||
			el.key === urlParams?.groupkey,
	)

	const header_title = getHeaderTitle(
		location.pathname,
		urlParams,
		rationInfo,
		currentGroupInfo?.title,
	)

	const handleBackClick = (fallbackPath: string, forcePath?: string) => {
		if (forcePath) {
			navigate(forcePath)
		} else if (previousLocationRef.current) {
			navigate(-1)
		} else {
			navigate(fallbackPath)
		}
	}

	const isRationListPage = location.pathname === '/feed/ration'
	const hasFoodIntegration = Boolean(userData?.company?.food_integration)
	const showCreateRation = isRationListPage && !hasFoodIntegration
	const isRationViewPage =
		Boolean(urlParams.rationid) &&
		location.pathname === `/feed/ration/${urlParams.rationid}`
	const rationIsInactive =
		Boolean(rationInfo) && (rationInfo?.groups?.length ?? 0) === 0
	const canEditCurrentRation =
		isRationViewPage &&
		Boolean(permissions?.can_edit_rations) &&
		!hasFoodIntegration &&
		Boolean(rationInfo?.id)
	const isComponentsTab =
		isRationListPage &&
		new URLSearchParams(location.search).get('tab') === 'components'
	const canManageRationComponents =
		Boolean(permissions?.can_edit_rations) &&
		!userData?.company?.food_integration

	useEffect(() => {
		if (!isComponentsTab) {
			setOpenAddComp(false)
		}
	}, [isComponentsTab])

	const isWarningStatus =
		cattleStatuses.treatmentStatus === InspectionStatus.Required ||
		cattleStatuses.treatmentStatus === TreatmentStatus.DisposeRecommended ||
		cattleStatuses.treatmentStatus === TreatmentStatus.Suspended ||
		cattleStatuses.fatnessStatus === 'attention'

	const isErrorCowStatus =
		cattleStatuses.treatmentStatus === TreatmentStatus.Treated ||
		cattleStatuses.fatnessStatus === 'critical'

	if (location.pathname === '/feed/analytics/feeding') {
		return null
	}

	return (
		<Box
			sx={{
				padding: '32px 16px',
				paddingLeft:
					location.pathname === `/feed/groups/${urlParams.groupkey}`
						? '26px'
						: '16px',
				width: '100%',
				position: 'sticky',
				top: 0,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				gap: '2px',
				backgroundColor: COLORS.background,
				zIndex: 100,
			}}
		>
			{location.pathname === `/feed` && <DashboardEditControls />}
			{location.pathname === `/profile/licenses` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/profile')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-12px', color: COLORS.icon }}
					/>
				</Box>
			)}
			{location.pathname === `/feed/groups/${urlParams.groupkey}` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/feed/groups', '/feed/groups')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-2px', color: COLORS.icon }}
					/>
				</Box>
			)}

			{(location.pathname === `/feed/ration/${urlParams.rationid}` ||
				location.pathname === '/feed/ration/create') && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/feed/ration')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-4px', color: COLORS.icon }}
					/>
				</Box>
			)}
			{location.pathname ===
				`/feed/ration/${urlParams.rationid}/version/${urlParams.versionid}` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick(`/feed/ration/${urlParams.rationid}`)}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-4px', color: COLORS.icon }}
					/>
				</Box>
			)}
			{location.pathname === `/feed/ration/${urlParams.rationid}/edit` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick(`/feed/ration/${urlParams.rationid}`)}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-4px', color: COLORS.icon }}
					/>
				</Box>
			)}
			{isCowPassportPage && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-4px', color: COLORS.icon }}
					/>
				</Box>
			)}
			{location.pathname === `/feed/deviations` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/feed')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-4px', color: COLORS.icon }}
					/>
				</Box>
			)}

			{location.pathname.startsWith('/settings/management') && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
						<Box
							sx={{ mt: '6px', cursor: 'pointer' }}
							onClick={() => handleBackClick('/settings/management')}
						>
							<ArrowBackIosIcon
								style={{ marginLeft: '-4px', color: COLORS.icon }}
							/>
						</Box>

						<TypographyHeaderExtra id={'headerTitle'}>
							{header_title}
						</TypographyHeaderExtra>

						{location.pathname === '/settings/management' &&
							userData?.superuser &&
							permissions?.can_manage_permissions && (
								<Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>
									<ButtonFunc
										title='Создать пользователя'
										onClick={() => setIsSettingUserOpen(true)}
									/>
								</Box>
							)}
					</Box>

					<SettingUser
						open={isSettingUserOpen}
						onClose={() => setIsSettingUserOpen(false)}
					/>
				</>
			)}
			{location.pathname === '/settings/planned-events' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/settings')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}

			{location.pathname === '/settings/milking-schedule' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/settings')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === `/feed/plan/history` ||
				(location.pathname === `/feed/plan/history/${urlParams.groupkey}` && (
					<Box
						sx={{ mt: '6px', cursor: 'pointer' }}
						onClick={() => handleBackClick('/feed/plan')}
					>
						<ArrowBackIosIcon
							style={{ marginLeft: '3px', color: COLORS.icon }}
						/>
					</Box>
				))}
			{location.pathname === `/feed/plan/edit` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/feed/plan')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === `/cattles/digitization/${urlParams.cameraId}` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname ===
				`/cattles/deffered-digitization/${urlParams.cameraId}` && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === '/cattles/lameness' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-2px', color: COLORS.icon }}
					/>
				</Box>
			)}

			{location.pathname === '/cattles/fatness' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles')}
				>
					<ArrowBackIosIcon
						style={{ marginLeft: '-2px', color: COLORS.icon }}
					/>
				</Box>
			)}

			{location.pathname === '/cattles/trim' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles/lameness')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === '/cattles/treatment' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles/lameness')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname.includes('/cattles/lameness/') &&
				location.pathname.endsWith('/check-up') && (
					<Box
						sx={{ mt: '6px', cursor: 'pointer' }}
						onClick={() => leaveCheckupPage(navigate)}
					>
						<ArrowBackIosIcon style={{ color: COLORS.icon }} />
					</Box>
				)}

			{location.pathname === '/cattles/trim/edit' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles/trim')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === '/cattles/trim/check-up' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/cattles/trim')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}
			{location.pathname === '/feed/milk-yield' && (
				<Box
					sx={{ mt: '6px', cursor: 'pointer' }}
					onClick={() => handleBackClick('/feed')}
				>
					<ArrowBackIosIcon style={{ color: COLORS.icon }} />
				</Box>
			)}

			{location.pathname === '/milking-process/shifts' && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
						<TypographyHeaderExtra id={'headerTitle'}>
							{header_title}
						</TypographyHeaderExtra>
						<Box
							sx={{
								marginLeft: 'auto',
								flexShrink: 0,
								display: 'flex',
								alignItems: 'flex-start',
							}}
						>
							<Box
								onMouseEnter={() =>
									isAssignShiftDisabled && setShowAssignShiftHint(true)
								}
								onMouseLeave={() => setShowAssignShiftHint(false)}
								sx={{
									position: 'relative',
									display: 'inline-flex',
									flexDirection: 'column',
									alignItems: 'flex-end',
								}}
							>
								<ButtonFunc
									title='Назначить смену'
									disabled={isAssignShiftDisabled}
									onClick={() => {
										setAssignShiftInitialValues(null)
										setIsAssignShiftOpen(true)
									}}
								/>
								{isAssignShiftDisabled && (
									<Box
										sx={{
											position: 'absolute',
											top: 'calc(100% + 6px)',
											right: 0,
											backgroundColor: '#FFFFFF',
											boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.08)',
											borderRadius: '12px',
											px: '12px',
											py: '10px',
											zIndex: 120,
											opacity: showAssignShiftHint ? 1 : 0,
											transform: showAssignShiftHint
												? 'translateY(0)'
												: 'translateY(-4px)',
											transition: 'opacity 160ms ease, transform 160ms ease',
											pointerEvents: 'none',
											whiteSpace: 'nowrap',
										}}
									>
										<TypographySecondary14 sx={{ color: '#A0A0A0' }}>
											Добавьте дояра, чтобы назначить смену
										</TypographySecondary14>
									</Box>
								)}
							</Box>
						</Box>
					</Box>
					<AssignShiftDrawer
						open={isAssignShiftOpen}
						onClose={() => setIsAssignShiftOpen(false)}
						initialValues={assignShiftInitialValues}
					/>
				</>
			)}
			{!location.pathname.startsWith('/settings/management') &&
				location.pathname !== '/milking-process/shifts' && (
					<TypographyHeaderExtra
						id={'headerTitle'}
						sx={showUndigitizedMark ? { color: COLORS.error } : undefined}
					>
						{header_title}
					</TypographyHeaderExtra>
				)}
			{(location.pathname === '/feed/plan' ||
				location.pathname.startsWith('/feed/plan/history')) && (
				<Box
					id={
						location.pathname === '/feed/plan'
							? 'feed-plan-header-actions'
							: 'feed-history-header-actions'
					}
					sx={{
						marginLeft: 'auto',
						display: 'flex',
						alignItems: 'center',
						flexShrink: 0,
					}}
				/>
			)}

			{showCreateRation && !isComponentsTab && (
				<Box sx={{ marginLeft: 'auto' }}>
					<ButtonFunc
						title='Создать рацион'
						onClick={() => navigate('/feed/ration/create')}
					/>
				</Box>
			)}
			{isComponentsTab && canManageRationComponents && (
				<Box sx={{ marginLeft: 'auto' }}>
					<ButtonFunc
						title='Добавить компонент'
						onClick={() => setOpenAddComp(true)}
					/>
				</Box>
			)}
			{isComponentsTab && (
				<PopupRationAddComp
					openAddComp={openAddComp}
					setOpenAddComp={setOpenAddComp}
				/>
			)}
			{/* {header_title === 'Ферма' && (
				<Box sx={{ position: 'absolute', bottom: '14px' }}>
					<TypographySecondary sx={{ width: '200px' }}>
						Сегодня, {today} {hour < 10 ? `0${hour}` : hour}:
						{minute < 10 ? `0${minute}` : minute}
					</TypographySecondary>
				</Box>
			)} */}
			{isRationViewPage && (
				<Box sx={{ marginLeft: '8px' }}>
					<ActiveComp is_archived={rationIsInactive} />
				</Box>
			)}
			{canEditCurrentRation && (
				<Box sx={{ marginLeft: 'auto', marginRight: '20px' }}>
					<TooltipHover title='Редактировать рацион' placement='bottom' oneLine>
						<EditLogo
							width='24px'
							height='24px'
							style={{ cursor: 'pointer' }}
							onClick={() => navigate(`/feed/ration/${rationInfo?.id}/edit`)}
						/>
					</TooltipHover>
				</Box>
			)}
			{location.pathname === `/feed/groups/${urlParams.groupkey}` && (
				<Stack direction={'row'} alignItems={'center'} gap={'10px'} ml={'8px'}>
					<TypographySecondary14>
						{currentGroupInfo?.barntitle}
					</TypographySecondary14>
					<StatusGroupLabels status={currentGroupInfo?.status} />
				</Stack>
			)}
			{isCowPassportPage && (
				<Box
					sx={{
						marginLeft: '12px',
						display: ' flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '4px',
					}}
				>
					{showUndigitizedMark && (
						<Tooltip
							title='Корова не оцифрована.'
							arrow
							componentsProps={{
								tooltip: {
									sx: {
										backgroundColor: '#fff',
										color: COLORS.error,
										boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
									},
								},
								arrow: {
									sx: {
										color: '#fff',
									},
								},
							}}
						>
							<Box display='flex'>
								<UndigitizedIcon width={24} height={24} />
							</Box>
						</Tooltip>
					)}
					<TagNumberIcon width={'30px'} height={'30px'} />
					<TypographyHeaderExtra sx={{ color: COLORS.black }}>
						{urlParams.tagNumber}
					</TypographyHeaderExtra>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: '10px',
							marginLeft: '18px',
						}}
					>
						{(cattleStatuses.yieldStatus === 'dry' ||
							cattleStatuses.yieldStatus === 'rejected') && (
							<LactationLogo
								color={yieldStatusColor[cattleStatuses.yieldStatus]}
							/>
						)}
						{(isErrorCowStatus || isWarningStatus) && (
							<HealthLogo
								fill={isErrorCowStatus ? COLORS.error : COLORS.health}
								stroke={isErrorCowStatus ? COLORS.error : COLORS.health}
							/>
						)}
						{cattleStatuses.treatmentStatus ===
							TreatmentStatus.DisposeRecommended && (
							<InfoChipItem
								bgColor={COLORS.error10}
								label={'Рекомендована на выбытие'}
								labelColor={COLORS.error}
							/>
						)}
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default Header
